<template>
  <div>
    <div class="text-[12px] leading-[16px] text-gray-500 mb-3">
      Page {{ formatNumber(segment.page) }}
    </div>

    <div
      class="border border-gray-400/25 relative rounded-[3px] overflow-hidden"
    >
      <div class="relative">
        <img
          ref="imageRef"
          :src="segment.url"
          loading="lazy"
          decoding="async"
          class="w-full pointer-events-none"
          @load="onImageLoad"
        />

        <MatchTag
          :id="number"
          :level="match.level"
          class="absolute -translate-x-3 -translate-y-4 z-10"
          :style="{
            top: `${tagPosition.top}%`,
            left: `${tagPosition.left}%`,
          }"
        />

        <!-- The viewBox carries the page's aspect so the scale stays uniform;
             the svg is a size container so the 2px token can be pre-divided by
             that scale. Do not go back to a square viewBox plus
             non-scaling-stroke: Chromium resolves non-scaling strokes against
             device pixels (half width on hidpi), and the square viewBox made
             horizontal edges ~30% thicker than vertical ones. -->
        <svg
          v-if="svgPath"
          class="absolute top-0 left-0 w-full h-full pointer-events-none overflow-visible [container-type:size]"
          :viewBox="`0 0 100 ${viewBoxHeight}`"
          preserveAspectRatio="none"
        >
          <path
            :d="svgPath"
            class="[stroke-width:calc(2px*tan(atan2(100px,100cqw)))]"
            :class="pathClasses"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const TextMatchFragment = graphql(`
  fragment TextMatchFields on TextMatch {
    id
    location
    level
    markers {
      hash
      color
      shine
      visible
    }
    urls {
      page
      url
    }
    coords {
      x1
      x2
      y1
      y2
      page
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import MatchTag from './matches/MatchTag.vue'
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { formatNumber } from '@/shared/utils/number'
import { computed, ref } from 'vue'
import {
  generateBlockPath,
  groupCoordsIntoBlocks,
  highlightPaddingForDisplay,
} from '@/features/infringement/utils/coords'

const props = defineProps<{
  match: FragmentType<typeof TextMatchFragment>
  pageIndex: number
  number: number
  side?: 'registered' | 'infringing'
}>()

const match = useFragment(TextMatchFragment, props.match)

const segment = computed(() => {
  const docUrl = match.urls[props.pageIndex]
  return {
    page: docUrl.page,
    url: docUrl.url,
    coords: match.coords.filter((coord) => coord?.page === docUrl.page) || [],
  }
})

const imageRef = ref<HTMLImageElement | null>(null)
const imageWidth = ref(0)
const imageHeight = ref(0)
const svgPath = ref('')
const tagPosition = ref({ top: 0, left: 0 })

// Both path axes are normalised by width, so the 0-100 x range pairs with a
// 0-(100 * page aspect) y range and the viewBox matches the rendered box.
const viewBoxHeight = computed(() =>
  imageWidth.value > 0 ? (imageHeight.value / imageWidth.value) * 100 : 100
)

const pathClasses = computed(() => ({
  'stroke-gray-500 fill-gray-500/10': !match.level,
  'stroke-red-500 fill-red-500/10': match.level === ScoreLevel.High,
  'stroke-yellow-500 fill-yellow-500/10': match.level === ScoreLevel.Medium,
  'stroke-green-500 fill-green-500/10': match.level === ScoreLevel.Low,
}))

function onImageLoad() {
  if (!imageRef.value) return

  imageWidth.value = imageRef.value.naturalWidth
  imageHeight.value = imageRef.value.naturalHeight

  const coords = segment.value.coords
  const blocks = groupCoordsIntoBlocks(coords)
  const paths: string[] = []
  const padding = highlightPaddingForDisplay(
    imageWidth.value,
    imageHeight.value,
    imageRef.value.clientWidth,
    imageRef.value.clientHeight
  )

  for (const block of blocks) {
    const pathData = generateBlockPath(
      block,
      imageWidth.value,
      imageWidth.value,
      0.3,
      padding
    )
    if (pathData) {
      paths.push(pathData)
    }
  }

  svgPath.value = paths.join(' ')

  if (coords.length > 0) {
    const coord = coords.reduce((min, coord) =>
      coord.y1 < min.y1 ? coord : min
    )
    tagPosition.value = {
      top: (coord.y1 / imageHeight.value) * 100,
      left: (coord.x1 / imageWidth.value) * 100,
    }
  }
}
</script>
